import React, { useState, useRef, useEffect } from 'react';
import {
  Drawer,
  Button,
  Input,
  Space,
  Typography,
  message,
  Spin,
  Divider,
  Card,
  Tag,
} from 'antd';
import {
  RobotOutlined,
  SendOutlined,
  CheckOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { characterApi } from '@/services/api/character';
import type { AiAnalyzeResult, GenerateRequest } from '@/services/api/character';

const { Title, Text, Paragraph } = Typography;

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  suggestedParams?: AiAnalyzeResult;
  timestamp: number;
}

interface AiAssistantDrawerProps {
  visible: boolean;
  onClose: () => void;
  onApplyParams: (params: Partial<GenerateRequest>) => void;
}

const AiAssistantDrawer: React.FC<AiAssistantDrawerProps> = ({
  visible,
  onClose,
  onApplyParams,
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      role: 'assistant',
      content: '您好！我是 AI 助手，可以帮您生成电子人角色。请告诉我您想要什么类型的角色，比如："我想要一个刘亦菲类型的古风女神"或者"生成一个赛博朋克风格的机械少女"。',
      timestamp: Date.now(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // 滚动到底部
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // 重置对话
  useEffect(() => {
    if (visible) {
      setMessages([
        {
          role: 'assistant',
          content: '您好！我是 AI 助手，可以帮您生成电子人角色。请告诉我您想要什么类型的角色，比如："我想要一个刘亦菲类型的古风女神"或者"生成一个赛博朋克风格的机械少女"。',
          timestamp: Date.now(),
        },
      ]);
      setInputValue('');
    }
  }, [visible]);

  // 发送消息
  const sendMessage = async () => {
    if (!inputValue.trim()) {
      message.warning('请输入内容');
      return;
    }

    const userMessage: ChatMessage = {
      role: 'user',
      content: inputValue.trim(),
      timestamp: Date.now(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setLoading(true);

    try {
      // 先调用分析接口
      const analyzeRes = await characterApi.analyzeRequest(userMessage.content, 'simple');

      if (analyzeRes.code === 200 && analyzeRes.data) {
        const assistantMessage: ChatMessage = {
          role: 'assistant',
          content: '好的，我已经根据您的描述生成了参数建议。您可以查看下方的参数预览，点击【应用参数】按钮即可填充到表单。',
          suggestedParams: analyzeRes.data,
          timestamp: Date.now(),
        };
        setMessages((prev) => [...prev, assistantMessage]);
        message.success('参数已生成，请查看建议');
      } else {
        throw new Error(analyzeRes.message || '分析失败');
      }
    } catch (error: any) {
      console.error('AI 分析失败:', error);
      const errorMessage: ChatMessage = {
        role: 'assistant',
        content: '抱歉，分析失败：' + (error.message || '未知错误'),
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, errorMessage]);
      message.error('分析失败');
    } finally {
      setLoading(false);
    }
  };

  // 处理回车发送
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // 应用参数
  const handleApplyParams = (params: AiAnalyzeResult) => {
    onApplyParams(params.fullParams);
    message.success('参数已应用到表单');
  };

  // 渲染消息
  const renderMessage = (msg: ChatMessage) => {
    const isUser = msg.role === 'user';
    return (
      <div
        key={msg.timestamp}
        style={{
          display: 'flex',
          justifyContent: isUser ? 'flex-end' : 'flex-start',
          marginBottom: 16,
        }}
      >
        <div
          style={{
            maxWidth: '85%',
            padding: '12px 16px',
            borderRadius: 12,
            background: isUser ? '#1890ff' : '#f0f0f0',
            color: isUser ? '#fff' : '#333',
            fontSize: 14,
            lineHeight: 1.5,
          }}
        >
          <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>

          {/* 渲染建议参数预览 */}
          {msg.suggestedParams && (
            <div style={{ marginTop: 12 }}>
              <Divider style={{ margin: '8px 0', borderColor: isUser ? 'rgba(255,255,255,0.3)' : '#e8e8e8' }} />
              <Card
                size="small"
                title={
                  <Space>
                    <RobotOutlined />
                    <Text strong={isUser}>建议参数</Text>
                  </Space>
                }
                style={{
                  background: isUser ? 'rgba(255,255,255,0.9)' : '#fff',
                  fontSize: 13,
                }}
                extra={
                  <Button
                    type="primary"
                    size="small"
                    icon={<CheckOutlined />}
                    onClick={() => handleApplyParams(msg.suggestedParams!)}
                  >
                    应用参数
                  </Button>
                }
              >
                <div style={{ fontSize: 12, color: '#666' }}>
                  <div style={{ marginBottom: 4 }}>
                    <Text strong>角色名称：</Text>{' '}
                    {msg.suggestedParams.name || '未设置'}
                  </div>
                  <div style={{ marginBottom: 4 }}>
                    <Text strong>分辨率：</Text>{' '}
                    {msg.suggestedParams.resolution || 'standard'}
                  </div>
                  <div style={{ marginBottom: 4 }}>
                    <Text strong>表情数量：</Text>{' '}
                    {msg.suggestedParams.selectedExpressions?.length || 0} 个
                  </div>
                  {msg.suggestedParams.enableHr && (
                    <div style={{ marginBottom: 4 }}>
                      <Tag color="blue">高清修复</Tag>
                    </div>
                  )}
                  {msg.suggestedParams.restoreFaces && (
                    <div style={{ marginBottom: 4 }}>
                      <Tag color="green">面部修复</Tag>
                    </div>
                  )}
                  <div style={{ marginTop: 8, fontSize: 11, color: '#999' }}>
                    {msg.suggestedParams.suggestion}
                  </div>
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Drawer
      title={
        <Space>
          <RobotOutlined style={{ color: '#1890ff' }} />
          <span>AI 助手</span>
        </Space>
      }
      placement="right"
      width={450}
      open={visible}
      onClose={onClose}
      closeIcon={null}
      styles={{
        body: {
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 60px)',
          padding: 0,
        },
      }}
      footer={
        <div style={{ padding: '12px', borderTop: '1px solid #f0f0f0' }}>
          <Space.Compact style={{ width: '100%' }}>
            <Input
              placeholder="描述您想要的角色类型..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={loading}
              allowClear
            />
            <Button
              type="primary"
              icon={loading ? <LoadingOutlined spin /> : <SendOutlined />}
              onClick={sendMessage}
              disabled={!inputValue.trim() || loading}
            >
              发送
            </Button>
          </Space.Compact>
        </div>
      }
    >
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px 20px',
          background: '#fafafa',
        }}
      >
        {messages.map(renderMessage)}
        <div ref={messagesEndRef} />
        {loading && (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <Spin tip="AI 正在思考..." />
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default AiAssistantDrawer;
